import { useState, useMemo } from 'react';
import {
  Stack,
  IconButton,
  Tooltip,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { AppCard } from '@/components/app-card/app-card';
import { AppDataGrid } from '@/components/ui';
import { GridAlignment } from '@mui/x-data-grid';
import { formatCurrencyNoUnit } from '@/lib/formatters';

interface RejectedResourcesProps {
  data: {
    users: any[];
    systems: any[];
    departments: any[];
    cloudAccounts: any[];
    resources: any[];
    resourceGroups: any[];
    rejectedResources: any[];
  };
  filteredRejectedResources: any[];
}

function RejectedResources({
  data,
  filteredRejectedResources,
}: RejectedResourcesProps) {
  const [selectedCloudAccount, setSelectedCloudAccount] = useState('');

  const handleCloudAccountChange = (event: SelectChangeEvent) => {
    setSelectedCloudAccount(event.target.value);
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const getFilteredResources = useMemo(() => {
    return (reason: 'Decommissioning' | 'Reassignment') => {
      return filteredRejectedResources
        .filter((resource) => resource.reason === reason)
        .filter((resource) => {
          if (selectedCloudAccount && selectedCloudAccount !== '') {
            return resource.cloudAccountId?.toString() === selectedCloudAccount;
          }
          return true;
        });
    };
  }, [filteredRejectedResources, selectedCloudAccount]);

  const getColumns = () => [
    {
      field: 'resourceName',
      headerName: 'Resource',
      flex: 1,
      renderCell: (params: any) => (
        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'resourceType',
      headerName: 'Resource type',
    },
    {
      field: 'subscription',
      headerName: 'Subscription',
    },
    {
      field: 'system',
      headerName: 'System',
    },
    {
      field: 'cost',
      headerName: 'Forecast',
      align: 'right' as GridAlignment,
      valueFormatter: (params: any) =>
        formatCurrencyNoUnit(Number(params.value || 0)),
    },
    {
      field: 'resourceOwner',
      headerName: 'Resource Owner',
    },
    {
      field: 'dateRejected',
      headerName: 'Date rejected',
      valueFormatter: (params: any) =>
        new Date(params.value).toLocaleDateString(),
    },
    {
      field: 'actions',
      headerName: '',
      width: 80,
      sortable: false,
      renderCell: (params: any) => (
        <Tooltip title="Copy resource name to clipboard">
          <IconButton
            size="small"
            onClick={() => copyToClipboard(params.row.resourceName)}
            color="primary">
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const DataGridContent = ({
    reason,
  }: {
    reason: 'Decommissioning' | 'Reassignment';
  }) => {
    const filteredResources = getFilteredResources(reason);

    return (
      <div style={{ height: 400, width: '100%' }}>
        <AppDataGrid
          rows={filteredResources}
          columns={getColumns()}
          getRowId={(row) => row.id}
        />
      </div>
    );
  };

  const cloudAccountHeaderRight = (
    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
      <FormControl size="small" sx={{ minWidth: 200 }}>
        <InputLabel>Cloud Account</InputLabel>
        <Select
          value={selectedCloudAccount}
          label="Cloud Account"
          onChange={handleCloudAccountChange}>
          <MenuItem value="">All</MenuItem>
          {data.cloudAccounts.map((cloudAccount) => (
            <MenuItem key={cloudAccount.id} value={cloudAccount.id.toString()}>
              {cloudAccount.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  );

  return (
    <Stack spacing={2}>
      <AppCard
        className="card100"
        title="Flagged for decommissioning"
        icon={<BarChartOutlinedIcon color="primary" />}
        headerRight={cloudAccountHeaderRight}>
        <DataGridContent reason="Decommissioning" />
      </AppCard>

      <AppCard
        className="card100"
        title="Flagged for reassignment"
        icon={<BarChartOutlinedIcon color="primary" />}
        headerRight={cloudAccountHeaderRight}>
        <DataGridContent reason="Reassignment" />
      </AppCard>
    </Stack>
  );
}

export default RejectedResources;
